import React, { useEffect, useState } from "react"
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import XLSX from 'xlsx-js-style'
import FilterSearch from 'pages/parts/FilterSearch'
import Pagination from 'pages/parts/Pagination'
import DeleteModal from 'pages/parts/DeleteModal'
import TableControl from 'pages/parts/TableControl'
import BarcodeScannerButton from 'pages/parts/BarcodeScannerButton'

import { PGET, DELETE, GET } from 'helpers/api'
import { styleExcelSheet } from 'helpers/excel'
import { checkRole, findFromArrayById, returnSign } from "helpers/helpers"

function Index() {
	const { t } = useTranslation()

	const reduxSettings = useSelector(state => state.settings)
	const reduxAccount = useSelector(state => state.account)

	const URL = `/services/web/api/product-group-pageList/`

	const canCreate = checkRole('POS_PRODUCT_GROUP_ADD')
	const canUpdate = checkRole('POS_PRODUCT_GROUP_EDIT')
	const canDelete = checkRole('POS_PRODUCT_GROUP_DELETE')

	const [pageCount, setPageCount] = useState(0)
	const [modalDelete, setModalDelete] = useState(false)
	const [modalTableControl, setModalTableControl] = useState(false)
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ posId: 0, productGroupId: 0, index: 0 })
	const [filterData, setFilterData] = useState({
		posId: reduxAccount.posId,
		search: '',
	})
	const [poses, setPoses] = useState([])
	const [data, setData] = useState([])

	async function deleteItem() {
		const response = await DELETE("/services/web/api/product-group/"
			+ deleteModalParams.posId + '/' + deleteModalParams.productGroupId, {}, { loader: true })
		if (response) {
			getData()
			setModalDelete(false)
		}
	}

	async function getData() {
		checkFilter()
		const response = await PGET(URL + filterData.posId, filterData)
		if (response && response.data) {
			setPageCount(Math.ceil((response?.headers['x-total-count']) / reduxSettings.size))
			setData(response.data)
			setDropdownIsOpen(false)
		}
	}

	function resetFilter() {
		setFilterData({
			...filterData,
			search: '',
		})
		getData()
	}

	function checkFilter() {
		if (
			filterData.posId === null &&
			filterData.search === ''
		) {
			setFilterDataExist(false)
		} else {
			setFilterDataExist(true)
		}
	}

	async function paginate(data) {
		const response = await PGET(URL + filterData.posId, { ...filterData, page: data.selected, size: reduxSettings.size })
		setData(response.data);
	}

	async function getPoses() {
		const response = await GET("/services/web/api/pos-helper", {}, { loader: true })
		if (response) {
			setPoses(response)
		}

	}

	async function exportToExcel() {
		let url = `/services/web/api/product-group-excel/${filterData.posId}`
		let params = ''
		if (filterData.search) params += returnSign(params) + 'search=' + filterData.search

		const response = await GET(url + params, {}, { loader: true })
		if (!response) return

		// Backend qatorlari guruhlab tartiblangan (bir guruh qatorlari ketma-ket).
		// Shu tartibda guruhga ketma-ket raqam (1, 2, 3...) beramiz -- Excel'da
		// qaysi qatorlar bir guruhga tegishli ekani ko'rinib tursin.
		const headers = [[
			'№',
			t('POS'),
			t('GROUP') + ' №',
			t('GROUP_NAME'),
			t('BARCODE') + ' (' + t('GROUP') + ')',
			t('NAME_OF_PRODUCT'),
			t('BARCODE'),
		]]

		let groupSeq = 0
		let lastGroupKey = null
		for (let i = 0; i < response.length; i++) {
			const row = response[i]
			const groupKey = row.posId + ':' + row.productGroupId
			if (groupKey !== lastGroupKey) {
				groupSeq++
				lastGroupKey = groupKey
			}
			headers.push([
				i + 1,
				row.posName,
				groupSeq,
				row.productGroupName,
				row.productGroupBarcode,
				row.productName,
				row.productBarcode,
			])
		}

		const ws = XLSX.utils.aoa_to_sheet(headers)
		styleExcelSheet(ws)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS")
		XLSX.writeFile(wb, t('GROUPING') + ".xlsx")
	}

	useEffect(() => {
		getPoses()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getData(true)
	}, [filterData.search]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">
					{t('GROUPING')}
				</h4>
				<div className="d-flex">
					{canCreate &&
						<Link className="btn-rounded btn btn-primary" to="/grouping/create">
							<i className="uil uil-plus me-2"></i>
							{t('CREATE')}
						</Link>
					}
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3">
						<div>
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}>
							</FilterSearch>
						</div>
						{/* Kamera scanner tugma (faqat telefonda) */}
						<BarcodeScannerButton
							className="me-2"
							onScan={(value) => setFilterData({ ...filterData, search: value, page: 0 })}>
						</BarcodeScannerButton>
						<div>
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
									{t('FILTER')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('POS')}</label>
											<Select
												options={poses}
												value={poses.find(option => option.id === filterData.posId) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'posId': option.id })}
												placeholder=""
												noOptionsMessage={() => t('LIST_IS_EMPTY')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end">
												<button type="button" className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('CANCEL')}</button>
												<button type="button" className="btn btn-primary" onClick={() => getData(true)}>{t('APPLY')}</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
						<div>
							<button type="button" className="btn btn-outline-primary btn-wide" onClick={exportToExcel}>
								EXCEL
							</button>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block mb-3">
							<div className="filter">
								<strong>{t('FILTER')}</strong>
								<br />
								{filterData.posId &&
									<span className="me-2">
										<b>{t('POS')}: </b> {findFromArrayById(poses, filterData.posId)}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('SEARCH')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('RESET')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-striped mb-0">
							<thead>
								<tr>
									{(!reduxSettings.tableGrouping || reduxSettings.tableGrouping?.pos) &&
										<th>{t('POS')}</th>
									}
									<th>{t('NAME_OF_PRODUCT')}</th>
									<th className="text-center">{t('BARCODE')}</th>
									<th className="text-center table-control">
										<i className="uil-setting table-control-icon" onClick={() => setModalTableControl(true)}></i>
										{t('ACTION')}
									</th>
								</tr>
							</thead>
							<tbody>
								{data.length > 0 ? data.map((item, index) => (
									<tr key={index}>
										{(!reduxSettings.tableGrouping || reduxSettings.tableGrouping?.pos) &&
											<td>{item.posName}</td>
										}
										<td>{item.productGroupName}</td>
										<td className="text-center">{item.productGroupBarcode}</td>
										<td className="d-flex justify-content-center">
											{canUpdate &&
												<Link to={`/grouping/update/${item.posId}/${item.productGroupId}`} className="me-2">
													<div className="table-action-button table-action-primary-button">
														<i className="uil-edit-alt"></i>
													</div>
												</Link>
											}
											{canDelete &&
												<div className="table-action-button table-action-danger-button"
													onClick={() => {
														setDeleteModalParams({ posId: item.posId, productGroupId: item.productGroupId, index: index });
														setModalDelete(true)
													}}>
													<i className="uil-times"></i>
												</div>
											}
										</td>
									</tr>
								)): <tr>
										<td colSpan="10" className="text-center">{t('NO_DATA')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>

					<Pagination pageCount={pageCount} paginate={paginate}></Pagination>
				</div>
			</div>

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>

			{/* MODAL TABLE CONTROL */}
			<TableControl
				modalTableControl={modalTableControl}
				setModalTableControl={setModalTableControl}
				keyName={'tableGrouping'}>
			</TableControl>
			{/* MODAL TABLE CONTROL */}
		</>
	)
}

export default Index
